import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { socket } from '../../libs/socket';
import { getOrCreateGroupClientId, setGroupClientDisplayName } from '../../utils/groupMeeting';

import { useGroupConnectionAwaiting } from '../group-connection-request';

import { getVisibleAwaitingParticipants, getVisibleConnectionRequests } from './meet.adapters';

interface UseMeetGroupFlowArgs {
  roomName: string;
  isGroupConsultationFlag: boolean;
  isPsychologist: boolean;
  localParticipantIdentity?: string;
  /** Identity участников, реально присутствующих в комнате (до фильтрации по очереди ожидания) */
  presentParticipantIds: string[];
  sendConnectionAllowed?: (id: string) => void;
}

export function useMeetGroupFlow({
  roomName,
  isGroupConsultationFlag,
  isPsychologist,
  localParticipantIdentity,
  presentParticipantIds,
  sendConnectionAllowed,
}: UseMeetGroupFlowArgs) {
  const {
    awaitingCount: awaitingFromDataChannel,
    onGroupConnectionRequest,
    awaitingList,
    removeAwaiting,
    clearAwaiting,
  } = useGroupConnectionAwaiting();

  const [groupParticipantsPanelOpen, setGroupParticipantsPanelOpen] = useState(false);
  const [groupPermissionRequested, setGroupPermissionRequested] = useState(false);
  const [connectionAllowedForGroupClient, setConnectionAllowedForGroupClient] = useState(false);
  const [dismissedConnectionRequestIds, setDismissedConnectionRequestIds] = useState<Set<string>>(() => new Set());
  const [hiddenAwaitingInPanelIds, setHiddenAwaitingInPanelIds] = useState<Set<string>>(() => new Set());
  const [allowedPendingIds, setAllowedPendingIds] = useState<Set<string>>(() => new Set());
  const [isCoOrganizer, setIsCoOrganizer] = useState(false);
  const [raisedHandParticipantIds, setRaisedHandParticipantIds] = useState<Set<string>>(() => new Set());
  const raisedHandParticipantIdsRef = useRef<Set<string>>(new Set());
  const canManageParticipants = isPsychologist || isCoOrganizer;
  const isLocalHandRaised = Boolean(localParticipantIdentity && raisedHandParticipantIds.has(localParticipantIdentity));

  // Пометки «скрыто» и «отклонено» живут ровно столько, сколько сам участник в очереди:
  // иначе identity из sessionStorage переживает перезагрузку страницы, и вернувшийся
  // человек навсегда остаётся невидимым для психолога.
  useEffect(() => {
    const awaitingIds = new Set(awaitingList.map((participant) => participant.identity));
    const keepKnown = (prev: Set<string>) => {
      const next = new Set(Array.from(prev).filter((id) => awaitingIds.has(id)));

      return next.size === prev.size ? prev : next;
    };

    setDismissedConnectionRequestIds(keepKnown);
    setHiddenAwaitingInPanelIds(keepKnown);
    setAllowedPendingIds(keepKnown);
  }, [awaitingList]);

  // Членство в сокет-комнате держится всё время, пока открыт экран групповой встречи,
  // и для клиента, и для психолога.
  // Раньше в зависимостях был `joined`, и переход в «вошёл» прогонял cleanup с 'leave'
  // уже после того, как useMeetSession отправлял свой 'join' — участник оказывался вне
  // комнаты и переставал получать поднятые руки и команды модерации. А психолог не входил
  // в комнату вовсе, пока сам не подключится, и запросы, поданные до этого, терялись.
  useEffect(() => {
    if (!roomName || !isGroupConsultationFlag) {
      return;
    }

    socket.emit('join', roomName);

    return () => {
      socket.emit('leave', roomName);
    };
  }, [roomName, isGroupConsultationFlag]);

  // Разрешение отправляется двумя независимыми каналами: сокет и data channel LiveKit.
  // Участник остаётся в очереди со статусом «разрешён» — он уйдёт оттуда только тогда,
  // когда действительно появится в комнате (см. эффект ниже). Так психолог видит,
  // что человек не дошёл, и может разрешить повторно.
  const allowConnection = useCallback(
    (id: string) => {
      setAllowedPendingIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
      sendConnectionAllowed?.(id);

      socket.emit('message', {
        roomId: roomName,
        message: {
          datetime: new Date().toISOString(),
          action: 'connection_allowed',
          data: { for: id },
        },
      });
    },
    [roomName, sendConnectionAllowed],
  );

  const handleAllowConnectionRequest = useCallback((id: string) => allowConnection(id), [allowConnection]);

  useEffect(() => {
    if (presentParticipantIds.length === 0) {
      return;
    }

    const present = new Set(presentParticipantIds);
    const arrived = awaitingList.filter((participant) => present.has(participant.identity));

    if (arrived.length === 0) {
      return;
    }

    arrived.forEach((participant) => removeAwaiting(participant.identity));
    setAllowedPendingIds((prev) => {
      const next = new Set(prev);
      arrived.forEach((participant) => next.delete(participant.identity));

      return next.size === prev.size ? prev : next;
    });
  }, [awaitingList, presentParticipantIds, removeAwaiting]);

  // Участник закрыл вкладку или потерял связь, так и не дождавшись входа (в том числе
  // уже разрешённый): LiveKit сам сообщает об этом через ParticipantDisconnected,
  // включая тех, кто ещё не был формально допущен в комнату. Убираем его из очереди
  // сразу — не нужно, чтобы психолог вручную разбирался, кто больше не на связи.
  const handleParticipantLeft = useCallback(
    (identity: string) => {
      removeAwaiting(identity);
    },
    [removeAwaiting],
  );

  const requestGroupPermission = useCallback(
    (displayName: string) => {
      setGroupClientDisplayName(roomName, displayName);
      const currentParticipant = getOrCreateGroupClientId(roomName);

      socket.emit('message', {
        roomId: roomName,
        message: {
          datetime: new Date().toISOString(),
          action: 'group_connection_request',
          data: { identity: currentParticipant.identity, displayName: currentParticipant.displayName },
        },
      });

      setGroupPermissionRequested(true);
    },
    [roomName],
  );

  const handleConnectionAllowed = useCallback(
    (identity?: string) => {
      const effectiveIdentity = identity ?? getOrCreateGroupClientId(roomName).identity;

      if (canManageParticipants) {
        removeAwaiting(effectiveIdentity);
      }

      if (!isPsychologist && effectiveIdentity === getOrCreateGroupClientId(roomName).identity) {
        setConnectionAllowedForGroupClient(true);
      }
    },
    [canManageParticipants, isPsychologist, removeAwaiting, roomName],
  );

  const dismissConnectionRequest = useCallback((id: string) => {
    // Только скрыть карточку уведомления; участник остаётся в списке ожидающих
    setDismissedConnectionRequestIds((prev) => new Set(Array.from(prev).concat(id)));
  }, []);

  const hideAwaitingParticipant = useCallback((id: string) => {
    setHiddenAwaitingInPanelIds((prev) => new Set(prev).add(id));
    // Скрытие из панели должно также убрать всплывашку уведомления про запрос
    setDismissedConnectionRequestIds((prev) => new Set(prev).add(id));
  }, []);

  const allowAllConnections = useCallback(() => {
    // Очередь не очищается: записи уйдут по мере реального подключения участников.
    // Раньше здесь был clearAwaiting(), из-за которого скрытые в панели участники
    // пропадали из очереди, так и не получив разрешения.
    awaitingList
      .filter((participant) => !hiddenAwaitingInPanelIds.has(participant.identity))
      .forEach((participant) => allowConnection(participant.identity));
  }, [allowConnection, awaitingList, hiddenAwaitingInPanelIds]);

  const handleRaisedHandChange = useCallback((payload: { identity: string; raised: boolean }) => {
    if (!payload.identity) {
      return;
    }

    const next = new Set(raisedHandParticipantIdsRef.current);

    if (payload.raised) {
      next.add(payload.identity);
    } else {
      next.delete(payload.identity);
    }

    raisedHandParticipantIdsRef.current = next;
    setRaisedHandParticipantIds(next);
  }, []);

  const toggleRaisedHand = useCallback(() => {
    if (!roomName || !localParticipantIdentity) {
      return;
    }

    const next = new Set(raisedHandParticipantIdsRef.current);
    const nextRaised = !next.has(localParticipantIdentity);

    if (nextRaised) {
      next.add(localParticipantIdentity);
    } else {
      next.delete(localParticipantIdentity);
    }

    raisedHandParticipantIdsRef.current = next;
    setRaisedHandParticipantIds(next);

    socket.emit('message', {
      roomId: roomName,
      message: {
        datetime: new Date().toISOString(),
        action: 'group_hand_status',
        data: { identity: localParticipantIdentity, raised: nextRaised },
      },
    });
  }, [localParticipantIdentity, roomName]);

  const syncRaisedHands = useCallback((participantIds: string[]) => {
    const prev = raisedHandParticipantIdsRef.current;
    const allowedIds = new Set(participantIds.filter(Boolean));
    const next = new Set(Array.from(prev).filter((identity) => allowedIds.has(identity)));

    if (next.size === prev.size && Array.from(next).every((identity) => prev.has(identity))) {
      return;
    }

    raisedHandParticipantIdsRef.current = next;
    setRaisedHandParticipantIds(next);
  }, []);

  const visibleConnectionRequests = useMemo(
    // Уже разрешённые карточки-уведомления не показываем повторно.
    () => getVisibleConnectionRequests(awaitingList, dismissedConnectionRequestIds, allowedPendingIds),
    [allowedPendingIds, awaitingList, dismissedConnectionRequestIds],
  );

  const visibleAwaitingParticipants = useMemo(
    () => getVisibleAwaitingParticipants(awaitingList, hiddenAwaitingInPanelIds, allowedPendingIds),
    [allowedPendingIds, awaitingList, hiddenAwaitingInPanelIds],
  );

  return {
    awaitingFromDataChannel,
    awaitingList,
    visibleConnectionRequests,
    visibleAwaitingParticipants,
    canManageParticipants,
    onGroupConnectionRequest,
    clearAwaiting,
    groupParticipantsPanelOpen,
    setGroupParticipantsPanelOpen,
    groupPermissionRequested,
    connectionAllowedForGroupClient,
    isCoOrganizer,
    isLocalHandRaised,
    raisedHandParticipantIds,
    setIsCoOrganizer,
    requestGroupPermission,
    handleAllowConnectionRequest,
    handleConnectionAllowed,
    handleParticipantLeft,
    handleRaisedHandChange,
    dismissConnectionRequest,
    hideAwaitingParticipant,
    allowAllConnections,
    toggleRaisedHand,
    syncRaisedHands,
  };
}
